import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import CommonHeader from '../../util_components/common_header';
import Button from '../../util_components/button';
import { post_api } from '../../../redux/api_funcs';
import * as actions from '../../../redux/action_creators';
import { get_api_url } from '../../../utils/urls';

// PROD-10109: active repeats that clash with another booking of the same teacher, e.g. after a DST change.
const IST = 'Asia/Kolkata';
const ALL = '';

const slot_label = (epoch, tz) => moment(epoch).tz(tz).format('ddd h:mm A z');

const target_label = (row) => {
  if (row.target_kind === 'repeat') return 'Repeat';
  const type = String(row.target_type || '');
  if (type.startsWith('YOGA')) return '1-on-1 session';
  if (type.includes('GROUP') || type.includes('WORKSHOP')) return 'Group class';
  return type;
};

const unique_options = (rows, value_key, label_key) => {
  const seen = new Map();
  rows.forEach((r) => seen.set(r[value_key], r[label_key]));
  return Array.from(seen, ([value, label]) => ({ value, label })).sort((a, b) =>
    String(a.label).localeCompare(String(b.label))
  );
};

// Compact stacked buttons; the shared LinkButton's padding overflows a narrow table cell.
const ACTION_LINK_STYLE = {
  display: 'block',
  margin: '0 0 6px',
  padding: '6px 10px',
  fontSize: '12px',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

function RepeatLink({ student_uuid, name }) {
  return (
    <a
      className="link-btn"
      style={ACTION_LINK_STYLE}
      href={`/repeat_sessions?uuid=${student_uuid}`}
      target="_blank"
      rel="noopener noreferrer"
      title={`Change repeat: ${name}`}
    >
      Change: {name}
    </a>
  );
}

RepeatLink.propTypes = {
  student_uuid: PropTypes.string.isRequired,
  name: PropTypes.string
};

RepeatLink.defaultProps = {
  name: ''
};

function ClashRow({ row }) {
  return (
    <div className="ct-row">
      <div className="ct-col ct-qf ct-sm-font">{row.tag === 'DST' ? 'DST' : 'Existing'}</div>
      <div className="ct-col ct-hf">
        <a className="link-no-dec" href={`/studentprofile?id=${row.student_uuid}`}>
          {row.student_name}
        </a>
        <div className="ct-sm-font">{row.student_timezone}</div>
      </div>
      <div className="ct-col ct-hf ct-sm-font">{row.teacher_name}</div>
      <div className="ct-col ct-hf ct-sm-font">
        <div>{slot_label(row.before_start, row.student_timezone)}</div>
        <div>{slot_label(row.before_start, IST)}</div>
      </div>
      <div className="ct-col ct-hf ct-sm-font">
        <div>{slot_label(row.after_start, row.student_timezone)}</div>
        <div>{slot_label(row.after_start, IST)}</div>
      </div>
      <div className="ct-col ct-hf ct-sm-font">
        <div>
          {target_label(row)}: {row.target_name}
        </div>
        <div>{slot_label(row.target_start, IST)}</div>
      </div>
      <div className="ct-col ct-qf ct-sm-font">
        <div>{moment(row.first_clash).tz(IST).format('D MMM YYYY')}</div>
        <div>{row.clash_count} clash dates</div>
      </div>
      <div className="ct-col ct-hf ct-sm-font" style={{ minWidth: 0 }}>
        <RepeatLink student_uuid={row.student_uuid} name={row.student_name} />
        {/* Either student of a repeat pair can move. */}
        {row.target_kind === 'repeat' && row.target_student_uuid ? (
          <RepeatLink student_uuid={row.target_student_uuid} name={row.target_name} />
        ) : null}
      </div>
    </div>
  );
}

ClashRow.propTypes = {
  row: PropTypes.shape({
    tag: PropTypes.string,
    student_uuid: PropTypes.string,
    student_name: PropTypes.string,
    student_timezone: PropTypes.string,
    teacher_name: PropTypes.string,
    before_start: PropTypes.number,
    after_start: PropTypes.number,
    target_kind: PropTypes.string,
    target_type: PropTypes.string,
    target_name: PropTypes.string,
    target_student_uuid: PropTypes.string,
    target_start: PropTypes.number,
    first_clash: PropTypes.number,
    clash_count: PropTypes.number
  }).isRequired
};

function RepeatClashReport() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [horizonEnd, setHorizonEnd] = useState(null);
  const [teacherUuid, setTeacherUuid] = useState(ALL);
  const [studentTimezone, setStudentTimezone] = useState(ALL);
  const [rows, setRows] = useState([]);

  const load_data = useCallback(() => {
    setLoading(true);
    post_api(get_api_url('get_repeat_clash_report'), {}, true)
      .then((res) => {
        const next = res.data.rows || [];
        setRows(next);
        // A filter whose value left the reloaded rows would hide every other clash.
        setTeacherUuid((prev) => (next.some((r) => r.teacher_uuid === prev) ? prev : ALL));
        setStudentTimezone((prev) => (next.some((r) => r.student_timezone === prev) ? prev : ALL));
        setHorizonEnd(res.data.to_ms || null);
        setLoaded(true);
      })
      .catch((e) => {
        const err_message =
          e?.response?.data?.message || 'Some error occured. Please contact dev team.';
        dispatch(actions.set_notification_variable(true, 'error', err_message));
      })
      .finally(() => setLoading(false));
  }, [dispatch]);

  useEffect(load_data, [load_data]);

  const teachers = useMemo(() => unique_options(rows, 'teacher_uuid', 'teacher_name'), [rows]);
  const timezones = useMemo(
    () => unique_options(rows, 'student_timezone', 'student_timezone'),
    [rows]
  );
  const visible = rows.filter(
    (r) =>
      (teacherUuid === ALL || r.teacher_uuid === teacherUuid) &&
      (studentTimezone === ALL || r.student_timezone === studentTimezone)
  );

  return (
    <CommonHeader loading={loading} title="Repeat Clashes" show_back>
      <div style={{ marginTop: '30px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end' }}>
          <Button onClick={load_data}>Refresh</Button>
          <label htmlFor="clash-teacher">
            Teacher
            <select
              id="clash-teacher"
              value={teacherUuid}
              onChange={(e) => setTeacherUuid(e.target.value)}
            >
              <option value={ALL}>All teachers</option>
              {teachers.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="clash-timezone">
            Student timezone
            <select
              id="clash-timezone"
              value={studentTimezone}
              onChange={(e) => setStudentTimezone(e.target.value)}
            >
              <option value={ALL}>All timezones</option>
              {timezones.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        {horizonEnd ? (
          <div className="ct-sm-font" style={{ marginTop: '12px' }}>
            All clashes from now until {moment(horizonEnd).tz(IST).format('D MMM YYYY')}
          </div>
        ) : null}
        <div className="cus-table" style={{ margin: '20px 0' }}>
          <div className="ct-row ct-h">
            <div className="ct-col ct-qf">Cause</div>
            <div className="ct-col ct-hf">Student</div>
            <div className="ct-col ct-hf">Teacher</div>
            <div className="ct-col ct-hf">Repeat before (student / IST)</div>
            <div className="ct-col ct-hf">Repeat after (student / IST)</div>
            <div className="ct-col ct-hf">Clashes with</div>
            <div className="ct-col ct-qf">First clash</div>
            <div className="ct-col ct-hf">Action</div>
          </div>
          {visible.map((row) => (
            <ClashRow
              key={`${row.repeat_uuid}-${row.target_kind}-${row.target_session_uuid || row.target_student_uuid}-${row.first_clash}`}
              row={row}
            />
          ))}
        </div>
        {loaded && visible.length === 0 ? <div>No upcoming clashes.</div> : null}
      </div>
    </CommonHeader>
  );
}

export default RepeatClashReport;
